import NextImage from 'next/image';
import styled, { css, keyframes } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

export const Wrapper = styled(Box)`
  position: fixed;
  /* Sit above any bottom-fixed third-party UI (cookie banner / chat bar);
     falls back to flush-bottom when there is none. Set by useBottomFixedOffset. */
  bottom: var(--sticky-pdp-bottom-offset, 0px);
  left: 0;
  width: 100%;

  ${({ theme }) => css`
    z-index: ${theme.zIndex.stickyPurchaseBar};
    background-color: ${theme.colors.white};
    border-top: 1px solid ${theme.colors.quaternaryGrey};
    box-shadow: 0px -3px 20px 0px rgba(0, 0, 0, 0.15);
    animation: ${slideUp} ${theme.transitions.duration.standard} ease;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `}
`;

export const Content = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;

  ${({ theme }) => css`
    /* Desktop: full-width bar with 40px side padding (Figma spec). */
    min-height: 84px;
    padding: ${theme.spacing(1.5)} ${theme.spacing(5)};

    ${theme.media.md.down} {
      /* Mobile: button-only strip. */
      padding: ${theme.spacing(2)};
    }
  `}
`;

// Product recap (thumb + name) — desktop only.
export const Recap = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;

  ${({ theme }) => theme.media.md.down} {
    display: none;
  }
`;

export const ImageWrapper = styled(Box)`
  position: relative;
  min-width: 56px;
  max-width: 56px;
  height: 56px;
`;

export const ProductImage = styled(NextImage)`
  object-fit: contain;
  inset: 0;
`;

export const Name = styled(Typography)`
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ theme }) => css`
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.h5};
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.almostBlack};
  `}
`;

// Price + CTA. On mobile the CTA fills the row (button only, per design).
export const Action = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};

  ${({ theme }) => theme.media.md.down} {
    width: 100%;
    gap: 0;
  }
`;

// Price hidden on mobile (button-only strip). Figma spec: amount 24px /
// currency 20px, Medium weight, primaryBlue (#0E2240). The Price component
// hardcodes its own colour/size, so override the rendered text here.
export const PriceWrapper = styled(Box)`
  ${({ theme }) => css`
    [data-test-id='PriceText'] {
      color: ${theme.colors.primaryBlue};
      font-weight: ${theme.fontWeights.medium};
      font-size: ${theme.fontSizes.h4Mobile};

      span {
        font-size: ${theme.fontSizes.h5};
      }
    }

    ${theme.media.md.down} {
      display: none;
    }
  `}
`;

// Fixed CTA width on desktop (232px, Figma); full width on mobile.
export const CtaWrapper = styled(Box)`
  ${({ theme }) => theme.media.md.up} {
    width: 232px;
  }

  ${({ theme }) => theme.media.md.down} {
    width: 100%;
  }
`;
